import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

// Shows the last ~30 background events: factory dispatches, task claims by
// autopilot, build status changes, health-check alerts. Read-only peripheral
// vision for "what's happening that I didn't do."
interface ActivityEvent {
  id: string;
  kind: 'dispatch' | 'task' | 'build' | 'health';
  title: string;
  detail: string;
  at: string;
  status?: string;
}

const ACTIVE_BUILD_STATUSES = new Set(['queued', 'running']);

export function ActivityPanel({ onClose }: { onClose: () => void }) {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const [dispatches, builds, claimed] = await Promise.all([
        supabase
          .from('factory_dispatches')
          .select('*')
          .gte('dispatched_at', since)
          .order('dispatched_at', { ascending: false })
          .limit(20),
        supabase
          .from('tb_builds')
          .select('*')
          .gte('created_at', since)
          .order('created_at', { ascending: false })
          .limit(20),
        supabase
          .from('tb_tasks')
          .select('*')
          .not('claimed_by', 'is', null)
          .gte('claimed_at', since)
          .order('claimed_at', { ascending: false })
          .limit(20),
      ]);

      if (!mounted) return;

      const merged: ActivityEvent[] = [];

      for (const d of (dispatches.data ?? []) as Array<Record<string, unknown>>) {
        merged.push({
          id: `d-${d.id as string}`,
          kind: 'dispatch',
          title: `dispatch → ${String(d.target_machine ?? '?')}`,
          detail: String(d.task_text ?? '').slice(0, 60),
          at: String(d.dispatched_at ?? ''),
          status: String(d.status ?? ''),
        });
      }
      for (const b of (builds.data ?? []) as Array<Record<string, unknown>>) {
        merged.push({
          id: `b-${b.id as string}`,
          kind: 'build',
          title: `build ${String(b.status ?? '')}`,
          detail: String(b.name ?? b.project_slug ?? ''),
          at: String(b.last_heartbeat ?? b.created_at ?? ''),
          status: String(b.status ?? ''),
        });
      }
      for (const c of (claimed.data ?? []) as Array<Record<string, unknown>>) {
        if (String(c.claimed_by) === 'me') continue;
        merged.push({
          id: `c-${c.id as string}`,
          kind: 'task',
          title: `${String(c.claimed_by)} claimed`,
          detail: String(c.title ?? ''),
          at: String(c.claimed_at ?? ''),
          status: String(c.status ?? ''),
        });
      }

      merged.sort((a, b) => b.at.localeCompare(a.at));
      setEvents(merged.slice(0, 30));
      setLoading(false);
    };

    load();

    const sub = supabase
      .channel('activity-panel')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'factory_dispatches' },
        (payload) => {
          const d = payload.new as Record<string, unknown>;
          setEvents((prev) => [
            {
              id: `d-${d.id as string}`,
              kind: 'dispatch',
              title: `dispatch → ${String(d.target_machine ?? '?')}`,
              detail: String(d.task_text ?? '').slice(0, 60),
              at: String(d.dispatched_at ?? new Date().toISOString()),
              status: String(d.status ?? ''),
            },
            ...prev,
          ]);
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tb_builds' },
        (payload) => {
          const b = payload.new as Record<string, unknown>;
          if (!b) return;
          setEvents((prev) => [
            {
              id: `b-${b.id as string}-${Date.now()}`,
              kind: 'build',
              title: `build ${String(b.status ?? '')}`,
              detail: String(b.name ?? b.project_slug ?? ''),
              at: String(b.last_heartbeat ?? b.created_at ?? new Date().toISOString()),
              status: String(b.status ?? ''),
            },
            ...prev.slice(0, 29),
          ]);
        },
      )
      .subscribe();

    return () => {
      mounted = false;
      sub.unsubscribe();
    };
  }, []);

  return (
    <aside
      className="w-[300px] shrink-0 border-l border-border bg-panel/70 backdrop-blur
                 flex flex-col h-full"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="text-xs font-mono tracking-widest text-amberHi">BACKGROUND</div>
        <button onClick={onClose} className="text-dim hover:text-ink text-xs font-mono">
          ×
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {loading && <div className="text-xs text-dim font-mono">loading…</div>}
        {!loading && events.length === 0 && (
          <div className="text-xs text-dim font-mono">no background activity</div>
        )}
        {events.map((e) => (
          <div key={e.id} className="panel-hi p-2.5 space-y-1">
            <div className="flex items-center gap-2">
              <span
                className={
                  'status-dot shrink-0 ' +
                  (e.status === 'failed' || e.status === 'down'
                    ? 'status-down'
                    : e.status === 'running' ||
                      e.status === 'queued' ||
                      ACTIVE_BUILD_STATUSES.has(e.status ?? '')
                    ? 'status-degraded'
                    : 'status-ok')
                }
              />
              <div className="text-[11px] font-mono text-ink truncate">{e.title}</div>
            </div>
            {e.detail && (
              <div className="text-[11px] text-muted truncate pl-4">{e.detail}</div>
            )}
            <div className="text-[10px] text-dim font-mono pl-4">{relativeTime(e.at)}</div>
          </div>
        ))}
      </div>
    </aside>
  );
}

function relativeTime(iso: string): string {
  if (!iso) return '—';
  const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}
